import { useEffect, useState } from "react";
import { Box, Button, Dialog, DialogActions, Stack, Typography } from "@mui/material";
import { grey } from "@mui/material/colors";
import moment from "moment";
import { datatypes, inputTypes, selectOptionsKeyName } from "data/globalData";
import { soHeaderColumnList, soHeaderEditableColumnList, syncSoDocumentDate } from "../data";
import SoColumnField from "../SoColumnField";
import { soPrimaryBtnSx, soSecondaryBtnSx } from "../soUi";

const formatDisplayValue = (value, column) => {
	if (value === undefined || value === null || value === "") return "—";
	if (column?.data_type === datatypes.DATE.key) {
		const m = moment(value);
		return m.isValid() ? m.format("DD/MM/YYYY") : String(value);
	}
	if (column?.input_type === inputTypes.SELECT.key) {
		const list = column?.[selectOptionsKeyName]?.list || [];
		const match = list.find((opt) => opt.key === value || String(opt.key) === String(value));
		if (match) return match.name;
		if (typeof value === "boolean") return value ? "Yes" : "No";
	}
	return String(value);
};

const DisplaySoHeaderDetails = ({ data, onSave, disableEdit = false }) => {
	const [modalOpen, setModalOpen] = useState(false);
	const [draft, setDraft] = useState(data || {});

	useEffect(() => {
		setDraft(syncSoDocumentDate(data || {}));
	}, [data]);

	const setDraftSynced = (updater) => {
		setDraft((prev) => {
			const next = typeof updater === "function" ? updater(prev) : updater;
			return syncSoDocumentDate(next);
		});
	};

	const handleClose = () => {
		setDraft(syncSoDocumentDate(data || {}));
		setModalOpen(false);
	};

	const handleSubmit = (e) => {
		e.preventDefault();
		if (typeof onSave === "function") onSave(syncSoDocumentDate(draft));
		setModalOpen(false);
	};

	return (
		<Box width="100%">
			<Box width="100%" display="flex" justifyContent="space-between" alignItems="center" pb={1.25}>
				<Typography sx={{ fontSize: 14, fontWeight: 700, color: "#111110", letterSpacing: "0.01em" }}>
					Main details
				</Typography>
				{!disableEdit && (
					<Button variant="contained" size="small" onClick={() => setModalOpen(true)} sx={soPrimaryBtnSx}>
						Edit
					</Button>
				)}
			</Box>

			<Box display="flex" flexWrap="wrap" columnGap="1%" width="100%">
				{soHeaderColumnList.map((column) => (
					<Box
						key={column.key}
						width={column.data_type === datatypes.LONGTEXT.key ? "49%" : "24%"}
						display="flex"
						border="1px solid white"
						boxSizing="border-box"
						pb={1}>
						<Box
							sx={{
								maxWidth: "42%",
								minWidth: "28%",
								borderRadius: "6px 0 0 6px",
								background: "#000",
								color: grey[50],
								display: "flex",
								alignItems: "center",
								p: "10px 2%",
							}}>
							<Typography variant="subtitle2" color={grey[200]} noWrap width="100%">
								{column.name}
							</Typography>
						</Box>
						<Box
							sx={{
								flex: 1,
								background: grey[100],
								overflow: "hidden",
								borderRadius: "0 6px 6px 0",
								display: "flex",
								alignItems: "center",
								p: "10px 2%",
							}}>
							<Typography
								variant="subtitle2"
								width="100%"
								sx={{
									fontWeight: 400,
									display: "-webkit-box",
									WebkitBoxOrient: "vertical",
									WebkitLineClamp: 3,
									overflow: "hidden",
								}}>
								{formatDisplayValue(data?.[column.key], column)}
							</Typography>
						</Box>
					</Box>
				))}
			</Box>

			<Dialog fullWidth maxWidth="lg" open={modalOpen} onClose={handleClose} component="form" onSubmit={handleSubmit}>
				<Box width="98%" p={2} boxSizing="border-box">
					<Typography variant="h6" width="100%" pl={1} mb={1}>
						Edit header details
					</Typography>
					<Stack direction="row" width="100%" flexWrap="wrap" columnGap="2%" rowGap={0.5}>
						{soHeaderEditableColumnList.map((column) => (
							<SoColumnField
								key={column.key}
								column={column}
								data={draft}
								setData={setDraftSynced}
								readOnly={!!column.disabled}
								width="32%"
							/>
						))}
					</Stack>
				</Box>
				<DialogActions>
					<Button variant="outlined" onClick={handleClose} sx={soSecondaryBtnSx}>
						Cancel
					</Button>
					<Button autoFocus variant="contained" type="submit" sx={{ ...soPrimaryBtnSx, mr: 1 }}>
						Save
					</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);
};

export default DisplaySoHeaderDetails;
